import React from 'react';

const Section6Evaluation = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-blue-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">📊</span>
      <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
        Model Evaluation Framework
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">A system to judge AI output: automated, multi-dimensional, real-time</p>

    {/* Three-layer evaluation system */}
    <div className="bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-2xl p-6 border border-blue-200/30 mb-6">
      <h3 className="text-base font-bold text-blue-600 uppercase tracking-wider mb-5 text-center">Three-Layer Evaluation Architecture</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          {
            layer: 'Layer 1',
            title: 'Automated Quality Scoring',
            icon: '🤖',
            tag: 'Real-time',
            items: ['5-dimension rubric (0-100)', 'Runs on every generation', 'Originality checked via RAG similarity', 'Grade assignment (A-F)']
          },
          {
            layer: 'Layer 2',
            title: 'User Feedback',
            icon: '👤',
            tag: 'Async',
            items: ['Star ratings (1-5)', 'Thumbs up/down', 'Save rate (generated vs saved)', 'Time spent per theme']
          },
          {
            layer: 'Layer 3',
            title: 'A/B Experimentation',
            icon: '🧪',
            tag: 'Statistical',
            items: ['Compares prompt variants across models', 'Welch\'s t-test significance', 'Cohen\'s d effect sizes', 'Confidence intervals']
          }
        ].map((layer, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-5 border border-blue-100/50 text-center">
            <span className="text-2xl block mb-2">{layer.icon}</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-blue-500 to-indigo-500 text-white">{layer.tag}</span>
            <p className="text-xs text-blue-500 font-bold uppercase tracking-wider mt-2">{layer.layer}</p>
            <h4 className="font-bold text-gray-800 text-sm mt-1 mb-3">{layer.title}</h4>
            <div className="space-y-1.5 text-left">
              {layer.items.map((item, j) => (
                <div key={j} className="flex items-start">
                  <span className="text-blue-500 text-xs mr-1.5 mt-0.5">✓</span>
                  <span className="text-sm text-gray-600">{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Quality scoring rubric */}
    <div className="bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-2xl p-6 border border-blue-200/30 mb-6">
      <h3 className="text-base font-bold text-blue-600 uppercase tracking-wider mb-5 text-center">5-Dimension Quality Scoring Rubric</h3>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5">
        {[
          { dim: 'Style Consistency', weight: '30%', desc: 'Detects 40+ conflicting keyword pairs (e.g., romantic + industrial), synonym overlap, and duplicates.', icon: '🎨' },
          { dim: 'Completeness', weight: '30%', desc: 'Checks theme name, description length, exactly 5 colors, 5-7 style keywords, and 5-10 key elements.', icon: '✅' },
          { dim: 'Originality', weight: '20%', desc: 'RAG search finds similar existing themes. High similarity lowers the score. Prevents generic output.', icon: '💡' },
          { dim: 'Actionability', weight: '15%', desc: 'Penalizes vague elements ("nice", "beautiful") and short descriptions. Rewards specific, implementable suggestions.', icon: '🎯' },
          { dim: 'Color Harmony', weight: '5%', desc: 'Checks color wheel harmony (complementary, analogous, triadic), contrast ratios, and saturation balance.', icon: '🌈' },
        ].map((d, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-blue-100/50 text-center">
            <span className="text-xl block mb-1">{d.icon}</span>
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-blue-500 to-indigo-500 text-white mb-2">{d.weight}</span>
            <p className="text-sm font-bold text-gray-800 mb-1">{d.dim}</p>
            <p className="text-xs text-gray-500 leading-relaxed">{d.desc}</p>
          </div>
        ))}
      </div>

      {/* Grade scale */}
      <div className="flex flex-wrap justify-center gap-2">
        {[
          { grade: 'A', range: '90-100', color: 'from-green-500 to-emerald-500' },
          { grade: 'B', range: '80-89', color: 'from-blue-500 to-cyan-500' },
          { grade: 'C', range: '70-79', color: 'from-yellow-500 to-amber-500' },
          { grade: 'D', range: '60-69', color: 'from-orange-500 to-red-400' },
          { grade: 'F', range: '<60', color: 'from-red-500 to-red-600' },
        ].map((g, i) => (
          <div key={i} className="flex items-center bg-white/80 rounded-lg px-3 py-2 border border-gray-200/50">
            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-r ${g.color} text-white text-xs font-bold mr-2`}>{g.grade}</span>
            <span className="text-sm text-gray-600">{g.range}</span>
          </div>
        ))}
      </div>
    </div>

    {/* User feedback signals */}
    <div className="bg-gradient-to-r from-blue-50/80 to-indigo-50/80 rounded-2xl p-5 border border-blue-200/30 mb-6">
      <h3 className="text-base font-bold text-blue-600 uppercase tracking-wider mb-3">🎯 Feedback Signals Tracked</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { signal: 'Star Ratings', type: 'Explicit', desc: '1-5 per theme, per model', icon: '⭐' },
          { signal: 'Thumbs Up/Down', type: 'Explicit', desc: 'Quick quality signal on generated themes', icon: '👍' },
          { signal: 'Save Rate', type: 'Implicit', desc: 'Generated vs saved ratio, per model', icon: '📥' },
          { signal: 'Time Spent', type: 'Implicit', desc: 'Per-page engagement duration', icon: '⏱️' },
        ].map((s, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-3 border border-blue-100/50 text-center">
            <span className="text-xl block mb-1">{s.icon}</span>
            <p className="text-sm font-bold text-gray-800">{s.signal}</p>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{s.type}</span>
            <p className="text-xs text-gray-400 mt-1">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Link to live dashboard */}
    <div className="text-center">
      <a href="/admin/evaluation" target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 transform hover:scale-105 shadow-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:shadow-blue-300/50 hover:shadow-xl">
        <span className="mr-2">📊</span>View Live Model Evaluation Dashboard →
      </a>
    </div>
  </section>
);

export default Section6Evaluation;
